import { FullPageLoader, NavigationAnnouncer, useScrollToTopOnNewPathname } from '@color/continuum';
import { retry, useQueryParamLocale } from '@color/lib';
import { Suspense, lazy, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import Smooch from 'smooch';

import { usePersistedQueryCookies } from 'lib/browser-state/usePersistedQueryCookies';
import { ADD_TO_CALENDAR_PATH } from 'ui/AddToCalendarPage/paths';
import { ADMIN_PORTAL_ROOT_PATH } from 'ui/AdminPortal/paths';
import { usePatientMessagingContext } from 'ui/App/Providers/PatientMessagingProvider';
import { AT_HOME_TEST_ACTIVATION_ROOT_PATH } from 'ui/AtHomePanelActivation/paths';
import { CARE_ROOT_PATH } from 'ui/Care/paths';
import { CLINICAL_PATHS } from 'ui/Clinical/paths';
import { isClinicalHost } from 'ui/Clinical/utils';
import { CONSULTS_ROOT_PATH } from 'ui/Consults/paths';
import { BASE_COVID_ADMIN_PATH } from 'ui/Covid/Admin/paths';
import { COVID_SITE_MANAGER_PATH } from 'ui/Covid/CovidSiteManager/paths';
import { COVID_ROOT_PATH } from 'ui/Covid/paths';
import { ESSENTIAL_CARE_ROOT_PATH } from 'ui/EssentialCare/paths';
import { PATIENT_MESSAGING_INBOX_PATH } from 'ui/PatientMessaging';
import { REQUISITION_COOKIE_NAME } from 'ui/Purchase/Order/state/PhysicianOrder/constants';
import { ALL_PURCHASE_PATHS } from 'ui/Purchase/paths';
import { REPORTS_V3_EMBEDDABLE_BASE_PATH, REPORTS_V3_PDF_BASE_PATH } from 'ui/Reports/paths';
import { BASE_RESEARCH_PATH } from 'ui/ResearchStudy/paths';
import { STI_ROOT_PATH } from 'ui/Sti/paths';
import { VACCINE_ROOT_PATH } from 'ui/Vaccine/paths';

import { DOCUMENTS_ROOT_PATH } from '../../../Documents/Upload/paths';
import { GeneticsEligibilityRedirect } from '../../../Purchase/PreOrder/Genetics/GeneticsEligibilityRedirect';
import { PRE_ORDER_GENETICS_REDIRECT_PATHS } from '../../../Purchase/PreOrder/Genetics/paths';
import { AngularReloader } from '../../Angular';
import { ANGULAR_EXACT_PATH_MATCHES } from '../../Angular/paths';
import { getRedirects } from '../../redirects/getRedirects';
import { useRepeatPathChangeDetection } from '../../utils/useRepeatPathChangeDetection';

const AddToCalendarPage = lazy(() =>
  retry(async () => ({
    default: (await import('ui/AddToCalendarPage/AddToCalendarPage')).AddToCalendarPage,
  }))
);
const AdminPortal = lazy(() => retry(() => import('../../../AdminPortal')));
// FIXME, rework these three COVID-19 routes to redirect to /covid/*
const CovidAdmin = lazy(() => retry(() => import('../../../Covid/Admin')));
const Vaccine = lazy(() => retry(() => import('../../../Vaccine')));
const AtHomeTestActivationRoutes = lazy(() => retry(() => import('ui/AtHomePanelActivation')));
const EssentialCareRoutes = lazy(() => retry(() => import('ui/EssentialCare')));
const StiRoutes = lazy(() => retry(() => import('ui/Sti')));
const CareRoutes = lazy(() =>
  retry(async () => ({
    default: (await import('ui/Care/Routes')).CareRoutes,
  }))
);
const ResearchPortal = lazy(() =>
  retry(async () => ({
    default: (await import('ui/ResearchStudy/ResearchPortal')).ResearchPortal,
  }))
);
const PurchaseContainer = lazy(() =>
  retry(async () => ({
    default: (await import('ui/Purchase')).PurchaseContainer,
  }))
);
const CovidRoutes = lazy(() =>
  retry(async () => ({
    default: (await import('ui/Covid/Routes')).CovidRoutes,
  }))
);
const HomePortal = lazy(() =>
  retry(async () => ({
    default: (await import('ui/Home/HomePortal')).HomePortal,
  }))
);
const DocumentUploadContainer = lazy(() =>
  retry(async () => ({
    default: (await import('../../../Documents/Upload/components/DocumentUploadContainer'))
      .DocumentUploadContainer,
  }))
);

// Typically we'd access reports via ColorPortal, but for printable and
// embeddable reports, we need to access it from the top level here so that we
// can skip common portal elements, such as the sticky header and footer.
const PrintableCmsReport = lazy(() =>
  retry(() => import('ui/Reports/components/PrintableCmsReport'))
);
const EmbeddableCmsReport = lazy(() =>
  retry(() => import('ui/Reports/components/EmbeddableCmsReport'))
);
// Require Auth Routes:
const Providers = lazy(() => retry(() => import('ui/ProviderPlatform/Providers')));
const ColorPortal = lazy(() => retry(() => import('../../ColorPortal')));
const ClinicalPortal = lazy(() => retry(() => import('ui/Clinical')));
const ConsultsRoutes = lazy(() => retry(() => import('ui/Consults')));
const PatientMessagingRoutes = lazy(() => retry(() => import('ui/PatientMessaging')));

const MARKETING_URL_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const AppPortals: React.FC = () => {
  usePersistedQueryCookies([REQUISITION_COOKIE_NAME, 'sampleId'], {
    legacyAngularSnakeCaseConversionIsEnabled: true,
  });

  /**
   * Marketing fields need to be first attribution.
   * So we only want to update them if there are none already saved.
   */
  usePersistedQueryCookies(MARKETING_URL_FIELDS, {
    firstWriteOnly: true,
  });
  useRepeatPathChangeDetection();
  const location = useLocation();
  useQueryParamLocale();
  // We need a component to pass into the `Smooch.render` method. We set it to a hidden div
  // via the `useRef` in the `PatientMessagingProvider` and store that ref in our context for usage
  // here.
  const { chatContainerRef } = usePatientMessagingContext();

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (chatContainerRef) {
      Smooch.render(chatContainerRef.current);
    }
  }, [chatContainerRef]);

  useScrollToTopOnNewPathname();

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Switch>
        {getRedirects(location)}

        {/*
          NOTE: If you are adding a new top-level route, you will likely need
          to add that route to react_urls.json as well.
        */}
        <Route exact path={ANGULAR_EXACT_PATH_MATCHES} component={AngularReloader} />
        <Route exact path="/" component={HomePortal} />
        <Route path={BASE_RESEARCH_PATH} component={ResearchPortal} />
        <Route path={DOCUMENTS_ROOT_PATH} component={DocumentUploadContainer} />
        <Route path={ALL_PURCHASE_PATHS} component={PurchaseContainer} />
        {/*
          The redirect for genetics eligibility verification for migrating populations
          from the old Angular flow to the new React flow. Once we are confident that
          it is ok to not service the old URLs, we can remove these redirects and
          the underlying code. For tracking removal, see PC-1557
        */}
        <Route
          exact
          path={PRE_ORDER_GENETICS_REDIRECT_PATHS}
          component={GeneticsEligibilityRedirect}
        />
        <Route
          exact
          path="/generalcatalyst"
          render={() => <GeneticsEligibilityRedirect company="generalcatalyst" />}
        />
        <Route
          exact
          path="/go/ohana"
          render={() => <GeneticsEligibilityRedirect company="salesforce" />}
        />
        <Route path={BASE_COVID_ADMIN_PATH} component={CovidAdmin} />
        <Route path={COVID_ROOT_PATH} component={CovidRoutes} />
        <Route path={ESSENTIAL_CARE_ROOT_PATH} component={EssentialCareRoutes} />
        <Route path={CARE_ROOT_PATH} component={CareRoutes} />
        <Route path={PATIENT_MESSAGING_INBOX_PATH} component={PatientMessagingRoutes} />
        <Route path={COVID_SITE_MANAGER_PATH}>
          {/* As of September 2024 we no longer support COVID site manager admin tooling */}
          <Redirect to="/" />
        </Route>
        <Route path={ADMIN_PORTAL_ROOT_PATH} component={AdminPortal} />
        <Route path="/providers" component={Providers} />
        <Route path={VACCINE_ROOT_PATH} component={Vaccine} />
        <Route path={CONSULTS_ROOT_PATH} component={ConsultsRoutes} />
        <Route path={STI_ROOT_PATH} component={StiRoutes} />
        <Route path={AT_HOME_TEST_ACTIVATION_ROOT_PATH} component={AtHomeTestActivationRoutes} />
        {isClinicalHost() && (
          <Route path={REPORTS_V3_EMBEDDABLE_BASE_PATH} component={EmbeddableCmsReport} />
        )}
        {isClinicalHost() && (
          <Route path={REPORTS_V3_PDF_BASE_PATH} component={PrintableCmsReport} />
        )}
        {isClinicalHost() && <Route path={CLINICAL_PATHS} component={ClinicalPortal} />}
        <Route exact path={ADD_TO_CALENDAR_PATH} component={AddToCalendarPage} />
        <Route component={ColorPortal} />
      </Switch>
      <NavigationAnnouncer />
    </Suspense>
  );
};
